import { Component, computed, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { format } from 'date-fns';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { trackByIndex } from '@beautyapp/core';
import { UserConsentHistoryService } from './user-consent-history.service';

@Component({
  selector: 'beautyapp-user-consent-history',
  templateUrl: './user-consent-history.component.html',
  styleUrl: './user-consent-history.component.scss',
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzTableModule,
    NzTagModule,
    NzSelectModule,
    NzDatePickerModule,
    NzButtonModule,
    NzFormModule
  ]
})
export class UserConsentHistoryComponent {
  userId = input.required<string>();

  private service: UserConsentHistoryService = inject(UserConsentHistoryService);
  private translateService: TranslateService = inject(TranslateService);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);

  protected readonly trackByIndex = trackByIndex;

  searchForm: UntypedFormGroup;
  date: (Date | null)[] | null = null;
  dateFormat = 'dd/MM/yyyy';

  // Consent kinds mirror the backend ConsentType enum.
  documentTypes = [
    { id: 'PROCEDURE_CONSENT', name: this.translateService.instant('users.consent.type.PROCEDURE_CONSENT') },
    { id: 'CUSTOMER_VERIFICATION', name: this.translateService.instant('users.consent.type.CUSTOMER_VERIFICATION') },
    { id: 'DATA_PROCESSING', name: this.translateService.instant('users.consent.type.DATA_PROCESSING') },
    { id: 'INFORMED_CONSENT', name: this.translateService.instant('users.consent.type.INFORMED_CONSENT') }
  ];

  verifiedOptions = [
    { id: 'true', name: this.translateService.instant('users.consent.verifiedYes') },
    { id: 'false', name: this.translateService.instant('users.consent.verifiedNo') }
  ];

  consentResource = rxResource({
    params: () => ({
      userId: this.userId(),
      page: this.page(),
      pageSize: this.pageSize(),
      searchValue: this.searchValue()
    }),
    stream: ({ params }) =>
      this.service.getHistory(params.userId, {
        page: params.page,
        pageSize: params.pageSize,
        documentType: params.searchValue.documentType,
        verified: params.searchValue.verified,
        startDate: params.searchValue.startDate,
        endDate: params.searchValue.endDate
      })
  });

  loadData = computed(() => this.consentResource.value()?.data || []);
  loader = computed(() => this.consentResource.isLoading());
  total = computed(() => this.consentResource.value()?.meta?.itemCount || 0);
  pageSize = signal(10);
  page = signal(1);
  searchValue = signal({} as any);

  constructor() {
    this.searchForm = this.fb.group({
      documentType: null,
      verified: null,
      startDate: null,
      endDate: null
    });
  }

  search() {
    this.searchValue.set(this.searchForm.value);
    this.page.set(1);
  }

  clearSearch() {
    this.searchForm.reset();
    this.date = null;
    this.searchValue.set({} as any);
    this.page.set(1);
  }

  changeCalendar($event: (Date | null)[]) {
    if (!$event || !$event.length) {
      this.searchForm.patchValue({ startDate: null, endDate: null });
      this.searchValue.set(this.searchForm.value);
      this.page.set(1);
      return;
    }

    const [start, end] = $event;
    this.searchForm.patchValue({
      startDate: start ? format(start, 'yyyy-MM-dd') : null,
      endDate: end ? format(end, 'yyyy-MM-dd') : null
    });
    this.searchValue.set(this.searchForm.value);
    this.page.set(1);
  }

  paginatorChange(pageEvent: any) {
    this.page.set(pageEvent.pageIndex);
    this.pageSize.set(pageEvent.pageSize);
  }
}
